import { Deserializer } from "jsonapi-serializer";
import createDebug from "debug";
import { DepartmentName } from "../models/DepartmentName";
import { createPartialURL, getFilterClause, getSortByClause } from "./utilities/urlBuilder";
import { DepartmentWrapper } from "../models/wrappers/DepartmentWrapper";
import { Department } from "../models/Department";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { APIConnector } from "./api/APIConnector";
import { PaginationModel } from "../models/Pagination";
import { createPagination } from "./utilities/paginationBuilder";
import { ListQuery } from "../models/ListQuery";
import { genericError } from "../models/GenericError";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { stateTypeDirectTable, typeSystemStats } from "../constants/api";

const apiConnector = new APIConnector();
const debug = createDebug("department-service");

interface MemberData {
    data: {
        type: string;
        id: string;
        relationships: {
            department?: {
                data?: {
                    type: string;
                    id?: string;
                };
            };
        };
    };
}

export const getDepartmentNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: DepartmentName[]; pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const departmentFields = "fields[department]=deptName,isDefault,site";
        const siteFields = "fields[site]=siteName";
        const url = `department?${paginationString}&include=site&${departmentFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }
            new Deserializer().deserialize(
                json,
                (err: any, department: any[]) => {
                    if (err) {
                        return reject({
                            status: response.status,
                            message: genericError,
                        });
                    }

                    const departments = department.map((d) => {
                        return {
                            value: d["id"],
                            label: d["dept-name"],
                            optionalColumns: [
                                {
                                    key: "site",
                                    value: d["site"]?.["site-name"],
                                },
                            ],
                            isDefault: d["is-default"],
                        };
                    });

                    return resolve({
                        rows: departments,
                        pagination: newPagination,
                    });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getDepartmentNames = async (): Promise<DepartmentName[]> => {
    try {
        const url = `department?fields[department]=deptName`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }
            new Deserializer().deserialize(
                json,
                (err: any, department: any[]) => {
                    if (err) {
                        return reject({
                            status: response.status,
                            message: genericError,
                        });
                    }

                    const departments: DepartmentName[] = department.map(
                        (d): DepartmentName => {
                            return {
                                value: d["id"],
                                label: d["dept-name"],
                            };
                        }
                    );

                    return resolve(departments);
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const buildDepartment = (d) => {
    return {
        id: d["id"],
        name: d["dept-name"],
        site: d["site"]["site-name"],
        enrolments: d["total-masters"],
        siteId: d["site"]["id"],
        default: d["dept-default"],
        isDefault: d["is-default"],
    };
};

export const getDepartmentById = async (id: number): Promise<Department> => {
    try {
        const departmentFields = 'fields[department]=isDefault,deptDefault,deptName,totalMasters,site';
        const siteFields = 'fields[site]=siteName';
        const url = `department/${id}?include=site&${departmentFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }
            new Deserializer().deserialize(
                json,
                (err: any, apiDepartment: any) => {
                    if (err) {
                        return reject({
                            status: response.status,
                            message: genericError,
                        });
                    }
                    const department = buildDepartment(apiDepartment);

                    return resolve(department);
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getDepartments = async (listParameters?: ListQuery): Promise<DepartmentWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsDepartment = "&fields[department]=isDefault,deptName,totalMasters,site";
        const fieldsSite = "&fields[site]=siteName";
        const url = `department?include=site${partialURL}&${fieldsDepartment}&${fieldsSite}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }
            new Deserializer().deserialize(
                json,
                (err: any, apiDepartments: any[]) => {
                    if (err) {
                        return reject({
                            status: response.status,
                            message: genericError,
                        });
                    }

                    const departments: Department[] = apiDepartments.map(
                        (d): Department => {
                            return buildDepartment(d);
                        }
                    );

                    return resolve({ rows: departments, pagination: pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getDepartmentsCountBySiteIds = async (siteIds: string[]): Promise<{ count: number }> => {
    try{
        const url = "";
        const headers = {
            type: typeSystemStats,
            statType: stateTypeDirectTable,
            tableName: "Department",
            filterBySiteids: siteIds
        };
        const response = await apiConnector.fetchData(url, true, headers);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            return resolve({ count: json.totals.countAll })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeDepartment = async (department: Department, createDepartment = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: "department",
                id: department.id,
                attributes: {
                    deptDefault: department.default ?? 0,
                    deptName: department.name,
                },
                relationships: {
                    site: {
                        data: {
                            type: "site",
                            id: department.siteId,
                        },
                    },
                },
            },
        };

        const url = `department${
            createDepartment ? "" : `/${newRecord.data.id}`
        }`;
        let response: Response;

        await apiConnector
            .sendData(url, createDepartment, newRecord)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                });
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createDepartment) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({
                            status: response.status,
                            message: getConstraintViolation(content.errors[0].detail),
                        });
                    }
                    new Deserializer().deserialize(
                        content,
                        (error, newDepartment) => {
                            if (error) {
                                return reject({
                                    status: response.status,
                                    id: department.id,
                                    success: false,
                                    message: genericError,
                                });
                            }
                            return resolve({
                                id: newDepartment.id,
                                success: true,
                            });
                        }
                    );

                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: department.id, success: true });
                }

                const content = await response.json();
                return reject({
                    status: response.status,
                    id: department.id,
                    success: false,
                    message: content.errors.map((error) => getConstraintViolation(error.detail)),
                });
            }
            return reject({
                status: INTERNAL_SERVER_ERROR,
                id: department.id,
                success: false,
                message: genericError,
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteDepartment = async (
    id: string
): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `department/${id}`;
        const department = {
            data: {
                type: "department",
                id: id,
            },
        };
        const response = await apiConnector.deleteData(url, department);
        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({
                status: response.status,
                id: id,
                success: false,
                message: getConstraintViolation(json.errors[0].detail),
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

/**
 * Builds a member data object with the given masterId and optional departmentId.
 * @param masterId The ID of the master.
 * @param departmentId The ID of the department (optional).
 * @returns The member data object.
 */
const buildMemberData = (
    masterId: string,
    departmentId?: string
): MemberData => {
    const memberData: MemberData = {
        data: {
            type: "master",
            id: masterId,
            relationships: {
                department: {},
            },
        },
    };

    if (departmentId) {
        memberData.data.relationships.department = {
            data: {
                type: "department",
                id: departmentId,
            },
        };
    }

    return memberData;
};

/**
 * Builds an array of member data objects to be saved with the given toBeAssigned array and departmentId.
 * @param toBeAssigned Array of member IDs to be assigned.
 * @param departmentId The ID of the department.
 * @returns Array of member data objects to be saved.
 */
const buildMembersToBeSaved = (
    toBeAssigned: string[],
    departmentId: string
) => {
    return toBeAssigned.map((masterId) =>
        buildMemberData(masterId, departmentId)
    );
};

/**
 * Builds an array of member data objects to be removed with the given toBeAssigned array.
 * @param toBeAssigned Array of member IDs to be unassigned.
 * @returns Array of member data objects to be removed.
 */
const buildMembersToBeRemoved = (toBeAssigned: string[]) => {
    return toBeAssigned.map((masterId) => buildMemberData(masterId));
};

/**
 * Assigns members to a department by saving new members and removing existing members.
 * @param toBeAssigned Array of member IDs to be assigned.
 * @param toBeUnassigned Array of member IDs to be unassigned.
 * @param departmentId The ID of the department.
 * @returns A Promise that resolves with the results of the member requests.
 */
export const assignMembersToDepartment = async (
    toBeAssigned: string[],
    toBeUnassigned: string[],
    departmentId: string
) => {
    // Build arrays of members to be saved and removed
    const newMembers = buildMembersToBeSaved(toBeAssigned, departmentId);
    const membersToRemove = buildMembersToBeRemoved(toBeUnassigned);

    // Combine the arrays of members to perform the operations in a single request
    const allMembers = [...newMembers, ...membersToRemove];

    // Send requests to add new members and remove existing members
    const addedMembers = allMembers.map(async (member) => {
        const url = `master/${member.data.id}`;
        return await apiConnector.sendData(url, false, member);
    });

    // Wait for all the requests to settle and return the results
    return Promise.allSettled([...addedMembers]);
};
